import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Button,
  Form,
  Modal,
  Badge,
  Pagination,
  Dropdown,
} from "react-bootstrap";
import {
  FaSearch,
  FaEdit,
  FaTrash,
  FaEye,
  FaFileExport,
  FaHistory,
} from "react-icons/fa";
import { usersService } from "../../services/usersService";
import { showToast, getErrorMessage } from "../../utils/toast";

const PLAN_FILTER_OPTIONS = [
  "none",
  "FREE",
  "BASIC",
  "ESSENTIAL",
  "PRO",
  "ULTIMATE",
  "CUSTOM",
];

const toInt = (value, fallback = 0) => {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const getSubscriptionEndDate = (user) =>
  user?.subscription_end_date ||
  user?.subscription_expiration_date ||
  user?.subscription_next_billing_date ||
  null;

const SUBSCRIPTION_STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "cancelled", label: "Cancelled" },
  { value: "expired", label: "Expired" },
  { value: "halted", label: "Halted" },
  { value: "pending_payment", label: "Pending Payment" },
  { value: "suspended", label: "Suspended" },
  { value: "created", label: "Created" },
];

const formatSubscriptionStatusLabel = (status) => {
  if (!status) return "Not set";
  const match = SUBSCRIPTION_STATUS_OPTIONS.find(
    (opt) => opt.value === String(status).toLowerCase()
  );
  if (match) return match.label;
  return String(status)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const UsersListTab = () => {
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [planFilters, setPlanFilters] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [showApiKeyChange, setShowApiKeyChange] = useState(false);
  const [newApiKey, setNewApiKey] = useState("");
  const [resendingVerification, setResendingVerification] = useState(false);
  const [subscriptionEndDateTouched, setSubscriptionEndDateTouched] =
    useState(false);
  const [subscriptionStatusTouched, setSubscriptionStatusTouched] =
    useState(false);
  const [showApiLogsModal, setShowApiLogsModal] = useState(false);
  const [apiLogsUser, setApiLogsUser] = useState(null);
  const [apiLogs, setApiLogs] = useState([]);
  const [apiLogsLoading, setApiLogsLoading] = useState(false);
  const [apiLogsError, setApiLogsError] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage] = useState(10);

  // Fetch users on component mount
  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await usersService.getUsers();
      setUsers(response.users || []);
      setError(null);
    } catch (err) {
      console.error("Error fetching users:", err);
      setError("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const generateNewApiKey = () => {
    // Generate new API key with the format API_KEY + 24 character hex string (uppercase)
    const chars = "0123456789ABCDEF";
    let randomHex = "";
    for (let i = 0; i < 24; i++) {
      randomHex += chars[Math.floor(Math.random() * chars.length)];
    }
    return "API_KEY" + randomHex;
  };

  const normalizePlan = (plan) => {
    if (!plan || String(plan).trim() === "") return "none";
    const upper = String(plan).trim().toUpperCase();
    return upper === "NONE" ? "none" : upper;
  };

  const togglePlanFilter = (plan) => {
    setPlanFilters((prev) =>
      prev.includes(plan) ? prev.filter((p) => p !== plan) : [...prev, plan]
    );
  };

  const filteredUsers = users.filter((user) => {
    // FIXED: Use last_name instead of sur_name
    const fullName = `${user.first_name || ""} ${user.last_name || ""}`.trim();
    const matchesSearch =
      fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase());
    const userStatus = user.is_account_active ? "Active" : "Inactive";
    const matchesStatus = statusFilter === "all" || userStatus === statusFilter;
    const userPlan = normalizePlan(user.subscription_plan);
    const matchesPlan =
      planFilters.length === 0 || planFilters.includes(userPlan);
    return matchesSearch && matchesStatus && matchesPlan;
  });

  // Pagination calculations
  const totalUsers = filteredUsers.length;
  const totalPages = Math.ceil(totalUsers / usersPerPage);
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);

  // Reset to first page when search/filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, planFilters]);

  const handleViewApiLogs = async (user) => {
    setApiLogsUser(user);
    setShowApiLogsModal(true);
    setApiLogs([]);
    setApiLogsError(null);
    setApiLogsLoading(true);
    try {
      const response = await usersService.getUserApiLogs(user.id, {
        limit: 50,
      });
      setApiLogs(response?.data?.logs || []);
    } catch (err) {
      console.error("Error fetching user API logs:", err);
      setApiLogsError(getErrorMessage(err) || "Failed to load API logs");
    } finally {
      setApiLogsLoading(false);
    }
  };

  const getLogStatusBadgeVariant = (statusCode) => {
    if (statusCode >= 200 && statusCode < 300) return "success";
    if (statusCode >= 400 && statusCode < 500) return "warning";
    if (statusCode >= 500) return "danger";
    return "secondary";
  };

  const formatLogTime = (timestamp) => {
    if (!timestamp) return "N/A";
    return new Date(timestamp).toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata",
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const formatApiLimit = (value) => {
    if (value == null || Number.isNaN(Number(value))) return "N/A";
    return Number(value).toLocaleString();
  };

  const resetModalEditState = () => {
    setShowPasswordChange(false);
    setNewPassword("");
    setShowApiKeyChange(false);
    setNewApiKey("");
    setSubscriptionEndDateTouched(false);
    setSubscriptionStatusTouched(false);
  };

  const handleViewUser = (user) => {
    setSelectedUser(user);
    setIsEditing(false);
    setIsAdding(false);
    resetModalEditState();
    setShowModal(true);
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setIsEditing(true);
    setIsAdding(false);
    resetModalEditState();
    setShowModal(true);
  };

  const escapeCsvValue = (val) => {
    if (val == null) return "";
    const s = String(val);
    if (s.includes(",") || s.includes('"') || s.includes("\n")) {
      return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
  };

  const handleExportUsers = () => {
    const headers = [
      "ID",
      "First Name",
      "Last Name",
      "Email",
      "Phone",
      "Status",
      "Plan",
      "Email Verified",
      "API Limit",
      "API Used",
      "Join Date",
    ];
    const rows = users.map((user) => [
      user.id,
      user.first_name || "",
      user.last_name || "",
      user.email || "",
      user.mobile || "",
      user.is_account_active ? "Active" : "Inactive",
      user.subscription_plan || "none",
      user.email_verified ? "Verified" : "Unverified",
      user.monthly_api_limit ?? "",
      Math.max(0, (user.monthly_api_limit ?? 0) - (user.api_limit_used ?? 0)),
      user.created_at ? new Date(user.created_at).toLocaleDateString() : "",
    ]);
    const csvContent = [
      headers.map(escapeCsvValue).join(","),
      ...rows.map((row) => row.map(escapeCsvValue).join(",")),
    ].join("\n");
    const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `users_export_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    showToast.success("Users exported to CSV");
  };

  const handleAddNewUser = () => {
    setSelectedUser({
      id: "",
      first_name: "",
      last_name: "", // FIXED: Use last_name instead of sur_name
      email: "",
      password: "",
      mobile: "",
      is_account_active: true,
      subscription_plan: "none", // FIXED: Use subscription_plan instead of subscription_type
      email_verified: false,
      monthly_api_limit: 0, // FIXED: Default to 0 instead of 1000
      created_at: new Date().toISOString().split("T")[0],
    });
    setIsEditing(true);
    setIsAdding(true);
    setSubscriptionEndDateTouched(false);
    setShowModal(true);
  };

  const handleSaveUser = async () => {
    try {
      if (isAdding) {
        // Validate required fields for new user
        if (!selectedUser.email || !selectedUser.password) {
          showToast.warning("Email and password are required for new users");
          return;
        }

        // FIXED: Use last_name instead of sur_name
        if (!selectedUser.first_name || !selectedUser.last_name) {
          showToast.warning("First name and last name are required");
          return;
        }

        if (selectedUser.password.length < 6) {
          showToast.warning("Password must be at least 6 characters long");
          return;
        }

        // Create new user
        await usersService.createUser({
          first_name: selectedUser.first_name,
          last_name: selectedUser.last_name, // FIXED: Use last_name
          email: selectedUser.email,
          password: selectedUser.password,
          mobile: selectedUser.mobile,
          is_account_active: selectedUser.is_account_active,
          subscription_plan: selectedUser.subscription_plan, // FIXED: Use subscription_plan
          email_verified: selectedUser.email_verified,
          monthly_api_limit: selectedUser.monthly_api_limit,
        });

        showToast.success("User created successfully! 🎉");

        // Refresh the users list
        await fetchUsers();
      } else {
        // Update existing user
        const updateData = {
          first_name: selectedUser.first_name,
          last_name: selectedUser.last_name, // FIXED: Use last_name
          email: selectedUser.email,
          mobile: selectedUser.mobile,
          is_account_active: selectedUser.is_account_active,
          subscription_plan: selectedUser.subscription_plan, // FIXED: Use subscription_plan
          email_verified: selectedUser.email_verified,
          monthly_api_limit: selectedUser.monthly_api_limit,
          api_limit_used: selectedUser.api_limit_used,
        };

        if (subscriptionEndDateTouched) {
          updateData.subscription_end_date =
            selectedUser.subscription_end_date || null;
        }

        if (subscriptionStatusTouched && selectedUser.subscription_status) {
          updateData.subscription_status = String(
            selectedUser.subscription_status
          ).toLowerCase();
          if (selectedUser.subscription_record_id) {
            updateData.subscription_record_id =
              selectedUser.subscription_record_id;
          }
        }

        // Include password if it was changed
        if (selectedUser.newPassword) {
          updateData.password = selectedUser.newPassword;
        }

        // Include API key if it was changed
        if (selectedUser.newApiKey) {
          updateData.api_key = selectedUser.newApiKey;
        }

        if (selectedUser.custom_api_permissions !== undefined) {
          updateData.custom_api_permissions =
            selectedUser.custom_api_permissions;
        }

        await usersService.updateUser(
          selectedUser.id,
          updateData
        );

        showToast.success("User updated successfully! ✅");

        // Refresh the users list
        await fetchUsers();
      }

      setShowModal(false);
      setSelectedUser(null);
      setIsEditing(false);
      setIsAdding(false);
      setShowPasswordChange(false);
      setNewPassword("");
      setShowApiKeyChange(false);
      setNewApiKey("");
      setSubscriptionEndDateTouched(false);
      setSubscriptionStatusTouched(false);
    } catch (error) {
      console.error("Error saving user:", error);
      showToast.error("Failed to save user: " + getErrorMessage(error));
    }
  };

  const handleDeleteUser = (user) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  const handleResendVerification = async () => {
    if (!selectedUser?.id || selectedUser.email_verified) return;

    try {
      setResendingVerification(true);
      const response = await usersService.resendVerificationEmail(selectedUser.id);
      showToast.success(
        response.message || `Verification email sent to ${selectedUser.email}`
      );
    } catch (error) {
      console.error("Error resending verification email:", error);
      showToast.error(
        "Failed to resend verification email: " + getErrorMessage(error)
      );
    } finally {
      setResendingVerification(false);
    }
  };

  const confirmDeleteUser = async () => {
    try {
      if (!selectedUser) return;

      await usersService.deleteUser(selectedUser.id);
      showToast.success("User deleted successfully! 🗑️");

      // Refresh the users list
      await fetchUsers();

      // Close modal
      setShowDeleteModal(false);
      setSelectedUser(null);
    } catch (error) {
      console.error("Error deleting user:", error);
      showToast.error("Failed to delete user: " + getErrorMessage(error));
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      Active: "success",
      Inactive: "secondary",
      Suspended: "danger",
    };
    return <Badge bg={variants[status]}>{status}</Badge>;
  };

  const getPlanBadge = (plan) => {
    // FIXED: Handle different plan types properly
    const planColors = {
      none: "secondary",
      FREE: "warning",
      BASIC: "info",
      ESSENTIAL: "primary",
      PRO: "success",
      ULTIMATE: "danger",
      CUSTOM: "dark",
    };

    const planName = plan?.toUpperCase() || "NONE";
    return <Badge bg={planColors[planName] || "secondary"}>{planName}</Badge>;
  };

  return (
    <Container fluid className="p-4">
      <Row className="mb-4">
        <Col>
          <h2 className="mb-3">Users Management</h2>
          <p className="text-muted">
            Manage all registered users in the system.
          </p>
        </Col>
      </Row>

      {/* Search and Filter */}
      <Row className="mb-4">
        <Col md={5}>
          <Form.Group>
            <Form.Label>Search Users</Form.Label>
            <div className="input-group">
              <Form.Control
                type="text"
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <Button variant="outline-secondary">
                <FaSearch />
              </Button>
            </div>
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label>Status Filter</Form.Label>
            <Form.Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">All Status</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
              <option value="Suspended">Suspended</option>
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group>
            <Form.Label>Plan Filter</Form.Label>
            <Dropdown autoClose="outside" className="w-100">
              <Dropdown.Toggle
                variant="outline-secondary"
                className="w-100 text-start d-flex justify-content-between align-items-center"
                id="plan-filter-dropdown"
              >
                <span className="text-truncate">
                  {planFilters.length === 0
                    ? "All Plans"
                    : planFilters
                        .map((p) => (p === "none" ? "NONE" : p))
                        .join(", ")}
                </span>
              </Dropdown.Toggle>
              <Dropdown.Menu className="w-100 p-2" style={{ minWidth: "100%" }}>
                {PLAN_FILTER_OPTIONS.map((plan) => (
                  <Form.Check
                    key={plan}
                    type="checkbox"
                    id={`plan-filter-${plan}`}
                    label={plan === "none" ? "NONE" : plan}
                    checked={planFilters.includes(plan)}
                    onChange={() => togglePlanFilter(plan)}
                    className="mb-2"
                  />
                ))}
                {planFilters.length > 0 && (
                  <>
                    <Dropdown.Divider />
                    <Button
                      variant="link"
                      size="sm"
                      className="p-0"
                      onClick={() => setPlanFilters([])}
                    >
                      Clear selection
                    </Button>
                  </>
                )}
              </Dropdown.Menu>
            </Dropdown>
          </Form.Group>
        </Col>
      </Row>

      {/* Users Table */}
      <Card>
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Users List ({totalUsers})</h5>
            <div className="d-flex gap-2">
              <Button variant="outline-primary" onClick={handleExportUsers}>
                <FaFileExport className="me-2" />
                Export
              </Button>
              <Button variant="success" onClick={handleAddNewUser}>
                Add New User
              </Button>
            </div>
          </div>
        </Card.Header>
        <Card.Body>
          {loading ? (
            <div className="text-center p-4">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2">Loading users...</p>
            </div>
          ) : error ? (
            <div className="alert alert-danger text-center" role="alert">
              {error}
              <Button
                variant="outline-primary"
                className="ms-2"
                onClick={fetchUsers}
              >
                Retry
              </Button>
            </div>
          ) : (
            <Table responsive striped hover>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Status</th>
                  <th>Plan</th>
                  <th>Email Verified</th>
                  <th>Join Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentUsers.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="text-center">
                      No users found
                    </td>
                  </tr>
                ) : (
                  currentUsers.map((user) => (
                    <tr key={user.id}>
                      <td>{user.id}</td>
                      {/* FIXED: Use last_name instead of sur_name */}
                      <td>
                        {`${user.first_name || ""} ${
                          user.last_name || ""
                        }`.trim() || "N/A"}
                      </td>
                      <td>{user.email}</td>
                      <td>{user.mobile || "N/A"}</td>
                      <td>
                        {getStatusBadge(
                          user.is_account_active ? "Active" : "Inactive"
                        )}
                      </td>
                      {/* FIXED: Use subscription_plan instead of subscription_type */}
                      <td>{getPlanBadge(user.subscription_plan || "none")}</td>
                      <td>
                        <Badge bg={user.email_verified ? "success" : "warning"}>
                          {user.email_verified ? "Verified" : "Unverified"}
                        </Badge>
                      </td>
                      <td>{new Date(user.created_at).toLocaleDateString()}</td>
                      <td>
                        <Button
                          variant="outline-info"
                          size="sm"
                          className="me-1"
                          title="View user"
                          onClick={() => handleViewUser(user)}
                        >
                          <FaEye />
                        </Button>
                        <Button
                          variant="outline-warning"
                          size="sm"
                          className="me-1"
                          title="Edit user"
                          onClick={() => handleEditUser(user)}
                        >
                          <FaEdit />
                        </Button>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="me-1"
                          title="API logs"
                          onClick={() => handleViewApiLogs(user)}
                        >
                          <FaHistory />
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          title="Delete user"
                          onClick={() => handleDeleteUser(user)}
                        >
                          <FaTrash />
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="d-flex justify-content-between align-items-center mt-3">
          <div className="text-muted">
            Showing {indexOfFirstUser + 1} to{" "}
            {Math.min(indexOfLastUser, totalUsers)} of {totalUsers} users
          </div>
          <Pagination className="mb-0">
            <Pagination.First
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
            />
            <Pagination.Prev
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
            />

            {/* Show page numbers */}
            {Array.from({ length: totalPages }, (_, index) => {
              const page = index + 1;
              // Show first page, last page, current page, and pages around current page
              if (
                page === 1 ||
                page === totalPages ||
                (page >= currentPage - 1 && page <= currentPage + 1)
              ) {
                return (
                  <Pagination.Item
                    key={page}
                    active={page === currentPage}
                    onClick={() => setCurrentPage(page)}
                  >
                    {page}
                  </Pagination.Item>
                );
              } else if (page === currentPage - 2 || page === currentPage + 2) {
                return <Pagination.Ellipsis key={page} />;
              }
              return null;
            })}

            <Pagination.Next
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages}
            />
            <Pagination.Last
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
            />
          </Pagination>
        </div>
      )}

      {/* User Details Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {isAdding
              ? "Add New User"
              : isEditing
              ? "Edit User"
              : "User Details"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedUser && (
            <Form>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>First Name</Form.Label>
                    <Form.Control
                      type="text"
                      value={selectedUser.first_name || ""}
                      onChange={(e) =>
                        (isEditing || isAdding) &&
                        setSelectedUser({
                          ...selectedUser,
                          first_name: e.target.value,
                        })
                      }
                      readOnly={!isEditing && !isAdding}
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Last Name</Form.Label>
                    <Form.Control
                      type="text"
                      value={selectedUser.last_name || ""}
                      onChange={(e) =>
                        (isEditing || isAdding) &&
                        setSelectedUser({
                          ...selectedUser,
                          last_name: e.target.value,
                        })
                      }
                      readOnly={!isEditing && !isAdding}
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Email</Form.Label>
                    <Form.Control
                      type="email"
                      value={selectedUser.email || ""}
                      onChange={(e) =>
                        (isEditing || isAdding) &&
                        setSelectedUser({
                          ...selectedUser,
                          email: e.target.value,
                        })
                      }
                      readOnly={!isEditing && !isAdding}
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Mobile</Form.Label>
                    <Form.Control
                      type="tel"
                      value={selectedUser.mobile || ""}
                      onChange={(e) =>
                        (isEditing || isAdding) &&
                        setSelectedUser({
                          ...selectedUser,
                          mobile: e.target.value,
                        })
                      }
                      readOnly={!isEditing && !isAdding}
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Monthly API Limit</Form.Label>
                    <Form.Control
                      type="number"
                      value={selectedUser.monthly_api_limit || 0}
                      onChange={(e) =>
                        (isEditing || isAdding) &&
                        setSelectedUser({
                          ...selectedUser,
                          monthly_api_limit: toInt(e.target.value, 0),
                        })
                      }
                      readOnly={!isEditing && !isAdding}
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>API Calls Used</Form.Label>
                    <Form.Control
                      type="number"
                      value={Math.max(
                        0,
                        (selectedUser.monthly_api_limit || 0) -
                          (selectedUser.api_limit_used || 0)
                      )}
                      readOnly
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Remaining API Calls</Form.Label>
                    <Form.Control
                      type="number"
                      onChange={(e) =>
                        (isEditing || isAdding) &&
                        setSelectedUser({
                          ...selectedUser,
                          api_limit_used: toInt(e.target.value, 0),
                        })
                      }
                      value={selectedUser.api_limit_used || 0}
                      readOnly={!isEditing && !isAdding}
                    />
                    {(isEditing || isAdding) && (
                      <Form.Text className="text-muted">
                        Stored as remaining balance (not consumed count).
                      </Form.Text>
                    )}
                  </Form.Group>
                  {isAdding && (
                    <Form.Group className="mb-3">
                      <Form.Label>Password</Form.Label>
                      <Form.Control
                        type="password"
                        value={selectedUser.password || ""}
                        onChange={(e) =>
                          setSelectedUser({
                            ...selectedUser,
                            password: e.target.value,
                          })
                        }
                        placeholder="Enter password for new user"
                        required
                      />
                      <Form.Text className="text-muted">
                        Password will be encrypted before storing in database
                      </Form.Text>
                    </Form.Group>
                  )}
                  {isEditing && !isAdding && (
                    <Form.Group className="mb-3">
                      <Form.Label>Password</Form.Label>
                      {!showPasswordChange ? (
                        <div>
                          <Form.Control
                            type="text"
                            value="••••••••••••••••"
                            readOnly
                          />
                          <Button
                            variant="outline-warning"
                            size="sm"
                            className="mt-2"
                            onClick={() => setShowPasswordChange(true)}
                          >
                            Change Password
                          </Button>
                          <Form.Text className="text-muted d-block">
                            Current password is encrypted and hidden
                          </Form.Text>
                        </div>
                      ) : (
                        <div>
                          <Form.Control
                            type="password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            placeholder="Enter new password"
                          />
                          <div className="mt-2">
                            <Button
                              variant="success"
                              size="sm"
                              className="me-2"
                              onClick={() => {
                                if (newPassword.length >= 6) {
                                  setSelectedUser({
                                    ...selectedUser,
                                    newPassword: newPassword,
                                  });
                                  setShowPasswordChange(false);
                                } else {
                                  showToast.warning(
                                    "Password must be at least 6 characters long"
                                  );
                                }
                              }}
                            >
                              Confirm New Password
                            </Button>
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => {
                                setShowPasswordChange(false);
                                setNewPassword("");
                              }}
                            >
                              Cancel
                            </Button>
                          </div>
                          <Form.Text className="text-muted">
                            New password will be encrypted before saving
                          </Form.Text>
                        </div>
                      )}
                    </Form.Group>
                  )}
                  {!isAdding && (
                    <Form.Group className="mb-3">
                      <Form.Label>Join Date</Form.Label>
                      <Form.Control
                        type="text"
                        value={
                          selectedUser.created_at
                            ? new Date(
                                selectedUser.created_at
                              ).toLocaleDateString()
                            : "N/A"
                        }
                        readOnly
                      />
                    </Form.Group>
                  )}
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Subscription Plan</Form.Label>
                    <Form.Select
                      value={selectedUser.subscription_plan || "none"}
                      onChange={(e) =>
                        (isEditing || isAdding) &&
                        setSelectedUser({
                          ...selectedUser,
                          subscription_plan: e.target.value,
                        })
                      }
                      disabled={!isEditing && !isAdding}
                    >
                      <option value="none">None</option>
                      <option value="FREE">Free</option>
                      <option value="BASIC">Basic</option>
                      <option value="ESSENTIAL">Essential</option>
                      <option value="PRO">Pro</option>
                      <option value="ULTIMATE">Ultimate</option>
                      <option value="CUSTOM">Custom</option>
                    </Form.Select>
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Email Verified</Form.Label>
                    <Form.Select
                      value={selectedUser.email_verified ? "true" : "false"}
                      onChange={(e) =>
                        (isEditing || isAdding) &&
                        setSelectedUser({
                          ...selectedUser,
                          email_verified: e.target.value === "true",
                        })
                      }
                      disabled={!isEditing && !isAdding}
                    >
                      <option value="true">Verified</option>
                      <option value="false">Unverified</option>
                    </Form.Select>
                    {!isAdding && !selectedUser.email_verified && (
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="mt-2"
                        onClick={handleResendVerification}
                        disabled={resendingVerification}
                      >
                        {resendingVerification
                          ? "Sending Verification Email..."
                          : "Resend Verification Email"}
                      </Button>
                    )}
                  </Form.Group>
                  {!isAdding && (
                    <>
                      <Form.Group className="mb-3">
                        <Form.Label>Subscription Status</Form.Label>
                        {isEditing ? (
                          <>
                            <Form.Select
                              value={
                                selectedUser.subscription_status
                                  ? String(
                                      selectedUser.subscription_status
                                    ).toLowerCase()
                                  : "active"
                              }
                              onChange={(e) => {
                                setSubscriptionStatusTouched(true);
                                setSelectedUser({
                                  ...selectedUser,
                                  subscription_status: e.target.value,
                                });
                              }}
                            >
                              {SUBSCRIPTION_STATUS_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                  {opt.label}
                                </option>
                              ))}
                            </Form.Select>
                            {(selectedUser.subscription_status === "cancelled" ||
                              selectedUser.subscription_status ===
                                "suspended") && (
                              <Form.Text className="text-warning d-block mt-1">
                                Cancelled/Suspended billing record — user plan
                                and API limits above are controlled separately
                                in this form.
                              </Form.Text>
                            )}
                          </>
                        ) : (
                          <Form.Control
                            type="text"
                            value={formatSubscriptionStatusLabel(
                              selectedUser.subscription_status
                            )}
                            readOnly
                          />
                        )}
                      </Form.Group>
                      <Form.Group className="mb-3">
                        <Form.Label>Subscription End Date</Form.Label>
                        {isEditing ? (
                          <Form.Control
                            type="date"
                            value={
                              getSubscriptionEndDate(selectedUser)
                                ? new Date(getSubscriptionEndDate(selectedUser))
                                    .toISOString()
                                    .split("T")[0]
                                : ""
                            }
                            onChange={(e) => {
                              setSubscriptionEndDateTouched(true);
                              setSelectedUser({
                                ...selectedUser,
                                subscription_end_date: e.target.value || null,
                              });
                            }}
                          />
                        ) : (
                          <Form.Control
                            type="text"
                            value={
                              getSubscriptionEndDate(selectedUser)
                                ? new Date(
                                    getSubscriptionEndDate(selectedUser)
                                  ).toLocaleDateString()
                                : "Not set"
                            }
                            readOnly
                          />
                        )}
                      </Form.Group>
                    </>
                  )}
                  <Form.Group className="mb-3">
                    <Form.Label>Account Status</Form.Label>
                    <Form.Select
                      value={
                        selectedUser.is_account_active ? "Active" : "Inactive"
                      }
                      onChange={(e) =>
                        (isEditing || isAdding) &&
                        setSelectedUser({
                          ...selectedUser,
                          is_account_active: e.target.value === "Active",
                        })
                      }
                      disabled={!isEditing && !isAdding}
                    >
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                    </Form.Select>
                  </Form.Group>

                  {!isAdding && (
                    <Form.Group className="mb-3">
                      <Form.Check
                        type="checkbox"
                        label="Forward Rate API access (admin override for testing)"
                        checked={
                          !!selectedUser.custom_api_permissions?.forwardRates
                        }
                        onChange={(e) =>
                          isEditing &&
                          setSelectedUser({
                            ...selectedUser,
                            custom_api_permissions: {
                              ...(selectedUser.custom_api_permissions || {}),
                              forwardRates: e.target.checked,
                            },
                          })
                        }
                        disabled={!isEditing}
                      />
                    </Form.Group>
                  )}

                  {!isAdding && selectedUser.api_key && (
                    <Form.Group className="mb-3">
                      <Form.Label>API Key</Form.Label>
                      {!showApiKeyChange ? (
                        <div>
                          <Form.Control
                            type="text"
                            value={selectedUser.api_key}
                            readOnly
                            style={{
                              fontSize: "12px",
                              fontFamily: "monospace",
                            }}
                          />
                          {(isEditing || isAdding) && (
                            <Button
                              variant="outline-primary"
                              size="sm"
                              className="mt-2"
                              onClick={() => {
                                const newKey = generateNewApiKey();
                                setNewApiKey(newKey);
                                setShowApiKeyChange(true);
                              }}
                            >
                              Generate New API Key
                            </Button>
                          )}
                          <Form.Text className="text-muted d-block">
                            Current API key for authentication
                          </Form.Text>
                        </div>
                      ) : (
                        <div>
                          <Form.Control
                            type="text"
                            value={newApiKey}
                            readOnly
                            style={{
                              fontSize: "12px",
                              fontFamily: "monospace",
                              backgroundColor: "#e8f5e8",
                            }}
                          />
                          <div className="mt-2">
                            <Button
                              variant="success"
                              size="sm"
                              className="me-2"
                              onClick={() => {
                                setSelectedUser({
                                  ...selectedUser,
                                  newApiKey: newApiKey,
                                });
                                setShowApiKeyChange(false);
                              }}
                            >
                              Confirm New API Key
                            </Button>
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => {
                                setShowApiKeyChange(false);
                                setNewApiKey("");
                              }}
                            >
                              Cancel
                            </Button>
                          </div>
                          <Form.Text className="text-muted">
                            New API key generated. Click confirm to save
                            changes.
                          </Form.Text>
                        </div>
                      )}
                    </Form.Group>
                  )}
                  {!isAdding && (
                    <Form.Group className="mb-3">
                      <Form.Label>User ID</Form.Label>
                      <Form.Control
                        type="text"
                        value={selectedUser.id || ""}
                        readOnly
                      />
                    </Form.Group>
                  )}
                </Col>
              </Row>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            {isEditing || isAdding ? "Cancel" : "Close"}
          </Button>
          {(isEditing || isAdding) && (
            <Button variant="primary" onClick={handleSaveUser}>
              {isAdding ? "Add User" : "Save Changes"}
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete the user "
          {`${selectedUser?.first_name || ""} ${
            selectedUser?.last_name || ""
          }`.trim() || "N/A"}
          " ({selectedUser?.email})? This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmDeleteUser}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      {/* User API Logs Modal */}
      <Modal
        show={showApiLogsModal}
        onHide={() => setShowApiLogsModal(false)}
        size="xl"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FaHistory className="me-2" />
            API Logs —{" "}
            {`${apiLogsUser?.first_name || ""} ${
              apiLogsUser?.last_name || ""
            }`.trim() ||
              apiLogsUser?.email ||
              "User"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {apiLogsUser && (
            <p className="text-muted mb-3">
              Showing latest requests for{" "}
              <strong>{apiLogsUser.email}</strong> (ID: {apiLogsUser.id})
            </p>
          )}
          {apiLogsLoading ? (
            <div className="text-center py-4">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2 mb-0">Loading API logs...</p>
            </div>
          ) : apiLogsError ? (
            <div className="alert alert-danger" role="alert">
              {apiLogsError}
            </div>
          ) : apiLogs.length === 0 ? (
            <div className="text-center py-4">
              <FaHistory size={40} className="text-muted mb-3" />
              <p className="text-muted mb-0">
                No API requests found for this user.
              </p>
            </div>
          ) : (
            <div className="table-responsive">
              <Table striped hover size="sm" className="mb-0">
                <thead>
                  <tr>
                    <th>Endpoint</th>
                    <th>Method</th>
                    <th>Status</th>
                    <th>Response</th>
                    <th>Limit Left</th>
                    <th>Time (IST)</th>
                  </tr>
                </thead>
                <tbody>
                  {apiLogs.map((log) => (
                    <tr key={log.id || `${log.timestamp}-${log.endpoint}`}>
                      <td>
                        <code className="small">{log.endpoint || "N/A"}</code>
                      </td>
                      <td>
                        <Badge
                          bg={
                            log.method === "GET"
                              ? "primary"
                              : log.method === "POST"
                              ? "success"
                              : "secondary"
                          }
                        >
                          {log.method || "N/A"}
                        </Badge>
                      </td>
                      <td>
                        <Badge bg={getLogStatusBadgeVariant(log.statusCode)}>
                          {log.statusCode ?? "N/A"}
                        </Badge>
                      </td>
                      <td>
                        <span
                          className={
                            log.responseTime > 1000
                              ? "text-warning fw-bold"
                              : "text-success"
                          }
                        >
                          {log.responseTime || 0}ms
                        </span>
                      </td>
                      <td>{formatApiLimit(log.apiLimitAfter)}</td>
                      <td>
                        <small className="text-muted">
                          {formatLogTime(log.timestamp)}
                        </small>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-primary"
            disabled={apiLogsLoading || !apiLogsUser}
            onClick={() => apiLogsUser && handleViewApiLogs(apiLogsUser)}
          >
            Refresh
          </Button>
          <Button
            variant="secondary"
            onClick={() => setShowApiLogsModal(false)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default UsersListTab;
